import PissarraApp from './PissarraApp'
import ProvesApp from './interface/ProvesApp';
import GestioApp from './gestio/GestioApp';
import CalendarApp from './calendar/CalendarApp';

import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import NotificationsDashboard from './notifications/NotificationsDashboard';
import FeedbackApp from './feedback/FeedbackApp';
import EditorApp from './editor/EditorApp';

import { fetchAPI, getSubdomain, getServerEndpoint } from '../utils/utils';
import { prefetchCommissionInfo } from '../utils/stripeCommission';
import { getSessionToken, clearSession } from '../utils/sessionStorage';
import { createSessionSocket } from '../utils/sessionSocket';
import { loginUser, isLoggedIn, logoutUser } from '../utils/login';
import { 
    trackNavigation, 
    trackAppInitialized, 
    trackCastellersInfoLoaded, 
    trackUserLoginSuccess,
    trackSocketUserAssociated
} from '../utils/posthogEvents.js';
import { reportConnectionWarningDisplayed, trackConnectionIncidents } from '../utils/connectionTelemetry';
import LoginApp from './login/LoginApp';
import FormsApp from './forms/FormsApp';
import FillForm from './forms/fill/FillForm';
import StatsApp from './stats/StatsApp';
import EtiquetesApp from './etiquetes/EtiquetesApp';
import FitxarApp from './fitxar/FitxarApp';
import EnllaçosApp from './enllaços/EnllaçosApp';
import MerxandatgeApp from './merxandatge/MerxandatgeApp';
import QuotesApp from './quotes/QuotesApp';
import SponsorSuccess from './quotes/SponsorSuccess';
import NouPatrocinador from './quotes/NouPatrocinador';
import MapaAccionsApp from './mapa/MapaAccionsApp';
import RolsApp from './gestio/RolsApp';
import SubvencionsApp from './subvencions/SubvencionsApp';
import SSLCertErrorModal from './other/SSLCertErrorModal';
import CookieBanner from './other/CookieBanner';
import PaymentReminderBanner from './other/PaymentReminderBanner';
import StripeVerificationBanner from './other/StripeVerificationBanner';
import { loadPermissions } from '../utils/permissions';
import DeepLinkRedirect from './DeepLinkRedirect';
import { parseEventAppLinkPath } from '../utils/eventDeepLinks';

function Loading() {
    return (
        <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center', 
            height: '100vh'
        }}>
            <div style={{
                width: 40,
                height: 40,
                border: '4px solid #e0e0e0',
                borderTopColor: '#007bff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
            }} />
            <style>{`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
}
const COLLA = getSubdomain();

// Define socket with server-side
const io = require("socket.io-client");
const serverEndpoint = getServerEndpoint(COLLA);
const socket = createSessionSocket(io, serverEndpoint, getSessionToken);

export function NoSignal({ socket }) {
    const [noSignal, setNoSignal] = useState(false);

    useEffect(() => {
        const handleConnectError = () => {
            setNoSignal(true);
        };
        const handleConnect = () => {
            setNoSignal(false);
        };

        socket.on('connect_error', handleConnectError);
        socket.on('connect', handleConnect);

        return () => {
            socket.off('connect_error', handleConnectError);
            socket.off('connect', handleConnect);
        }
    }, [socket]);

    useEffect(() => {
        if (noSignal) reportConnectionWarningDisplayed(socket);
    }, [socket, noSignal]);

    return noSignal && (
        <div style={{ position: 'fixed', zIndex: 99999, left: 20, top: 20 }}>
            <div style={{ padding: 20, backgroundColor: 'rgba(220,220,220,0.95)', borderRadius: 10 }}>
                <div style={{ fontSize: 20 }}>🚫 📶</div>
            </div>
        </div>
    )
}  

function Interface(props) {
    const posthog = usePostHog();
    const [isLogged, setIsLogged] = useState(null);
    const [userId, setUserId] = useState(null);
    const [authenticationRequired, setAuthenticationRequired] = useState(false);
    const [userInfo, setUserInfo] = useState({})
    const [serverId, setServerId] = useState('');
    const [castellersInfo, setCastellersInfo] = useState({});
    const [certError, setCertError] = useState(false);
    const [, setPermissionsLoaded] = useState(false);

    const pathname = window.location.pathname;
    const params = pathname.split('/').filter(part => part != '');
    const eventDeepLink = parseEventAppLinkPath(window.location.pathname);
    const nonPissarraRoutes = ['gestio', 'calendar', 'notifications', 'feedback', 'editor', 'forms', 'f', 'stats', 'event', 'models', 'etiquetes', 'fitxar', 'links', 'botiga', 'quotes', 'mapa', 'rols', 'subvencions'];
    const isInPissarra = params.length >= 3 && !nonPissarraRoutes.includes(params[0]);
    const isInPissarraReadOnly = isInPissarra && params[3] !== 'edit';
    const isInProjector = params.length > 0 && ['esdeveniments', 'assaigs', 'activitats', 'actuacions'].includes(params[0]);
    const isInGestio = params.length > 0 && params[0] === 'gestio';
    const isInCalendar = params.length > 0 && params[0] === 'calendar';
    const isInNotifications = params.length > 0 && params[0] === 'notifications';
    const isInFeedback = params.length > 0 && params[0] === 'feedback';
    const isInEditor = params.length > 0 && params[0] === 'editor';
    const isInForms = params.length > 0 && params[0] === 'forms';
    const isInFormsPublic = params.length > 0 && params[0] === 'forms' && params[params.length - 1] === 'respostes';
    const isFillingForm = params.length > 0 && params[0] === 'f';
    const isInStats = params.length > 0 && params[0] === 'stats';
    const isInEventToEdit = params.length > 0 && params[0] === 'event';
    const isModels = params.length > 0 && params[0] === 'models';
    const isInEtiquetes = params.length > 0 && params[0] === 'etiquetes';
    const isInFitxar = params.length > 0 && params[0] === 'fitxar';

    useEffect(() => {
        // The tabs read the permissions while rendering: draw again once they arrive.
        loadPermissions().then(() => setPermissionsLoaded(true)).catch(() => {});
        prefetchCommissionInfo();
        fetch(`${serverEndpoint}/auth/preparation`).then(response => response.json())
            .then(config => setAuthenticationRequired(config.authenticationRequired === true)).catch(() => {});
    }, []);
    const isInEnllaços = params.length > 0 && params[0] === 'links';
    const isInMerxandatge = params.length > 0 && params[0] === 'botiga';
    const isInQuotes = params.length > 0 && params[0] === 'quotes';
    const isInMapaAccions = params.length > 0 && params[0] === 'mapa';
    const isInRols = params.length > 0 && params[0] === 'rols';
    const isInSubvencions = params.length > 0 && params[0] === 'subvencions';
    const isSponsorSuccess = params.length > 0 && params[0] === 'patrocini-success';
    const isNouPatrocinador = params.length > 0 && params[0] === 'nou-patrocinador';

    // Track navigation events
    useEffect(() => {
        trackNavigation(posthog, pathname.split('/').filter(Boolean), COLLA);
    }, [pathname, posthog]);

    const exports = {
        'socket': socket,
        'isLogged': isLogged,
        'setIsLogged': setIsLogged,
        'userId': userId,
        'setUserId': setUserId,
        'userInfo': userInfo,
        'setUserInfo': setUserInfo,
        'isInEventToEdit': isInEventToEdit,
        'isModels':  isModels,
        'serverId': serverId,
        'setServerId': setServerId,
        'castellersInfo': castellersInfo,
        'setCastellersInfo': setCastellersInfo,
        ...props
    };

    useEffect(() => {
        trackAppInitialized(posthog, COLLA, serverEndpoint);
    }, []);

    useEffect(() => {
        // The user's roles come from here: never from a copy saved in the browser.
        fetchAPI('/castellersInfo', (data) => {
            setCastellersInfo(data);
            trackCastellersInfoLoaded(posthog, COLLA);
        }, false)
    }, [isLogged])
    
    useEffect(() => {
        isLoggedIn()
            .then(res => {
                setIsLogged(res.isLogged);
                setUserId(res.userId);
                
                if (res.isLogged) {
                    trackUserLoginSuccess(posthog, COLLA);
                }
            });
    }, [])

    useEffect(() => {
        if (!isLogged) return;

        // Associate user with socket
        const associateUser = () => {
            socket?.emit('.associate_user', userId);
            trackSocketUserAssociated(posthog, COLLA, serverEndpoint);
        };

        // A fresh password login changes the credential used by the handshake.
        const token = getSessionToken();
        if (socket.sessionToken !== token) {
            socket.sessionToken = token;
            socket.disconnect().connect();
        } else associateUser();

        // Re-associate when socket reconnects
        socket?.on('connect', associateUser);

        return () => {
            socket?.off('connect', associateUser);
        }
    }, [isLogged, userId, socket, serverId])

    useEffect(() => {
        const rejected = error => {
            const code = error?.data?.error || error?.error;
            if (!['UNAUTHORIZED', 'REACTIVATION_REQUIRED'].includes(code)) return;
            clearSession();
            setIsLogged(false);
            setUserId(-1);
        };
        socket.on('.session_error', rejected);
        socket.on('connect_error', rejected);
        return () => { socket.off('.session_error', rejected); socket.off('connect_error', rejected); };
    }, []);

    useEffect(() => {
        if (isFillingForm) {
            return undefined;
        }

        const stopTracking = trackConnectionIncidents(socket, posthog, COLLA, serverEndpoint);
        const handleConnectError = (err) => {
            // Extract message or default to empty
            const msg = err && err.message ? err.message : '';
            
            // Consider certificate name mismatch or generic transport polling errors as cert errors
            if (msg.includes('CERT_COMMON_NAME_INVALID') || msg.toLowerCase().includes('xhr poll error')) {
                setCertError(true);
            }
        };

        socket.on('connect_error', handleConnectError);
        return () => {
            stopTracking();
            socket.off('connect_error', handleConnectError);
        };
    }, [isFillingForm, posthog]);

    if (eventDeepLink) {
        return <DeepLinkRedirect colla={COLLA} {...eventDeepLink} />;
    }

    return (<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {!isFillingForm && isLogged ? <NoSignal socket={socket} /> : null}
        {!isFillingForm ? <SSLCertErrorModal visible={certError} onClose={() => setCertError(false)} /> : null}
        <CookieBanner />
        <PaymentReminderBanner isLogged={isLogged} userInfo={userInfo} userId={userId} />
        <StripeVerificationBanner isLogged={isLogged} userInfo={userInfo} />
        {
            isSponsorSuccess ? <SponsorSuccess /> :
            isNouPatrocinador ? <NouPatrocinador /> :
            isFillingForm ? <FillForm {...exports} /> :
            authenticationRequired && isLogged !== true ? (isLogged === null ? <Loading /> : <LoginApp {...exports} allowMembers={isInProjector || isInPissarraReadOnly} />) :
            isInFormsPublic ? <FormsApp {...exports} isPublic={true} /> :
            isInProjector ? <PissarraApp {...exports} readonly={true} /> :
            isInEditor ? <EditorApp {...exports} /> :
            isInPissarraReadOnly ? <PissarraApp {...exports} readonly={true} /> :
            isLogged === null ? <Loading /> :
            isLogged === false ? <LoginApp {...exports} /> :
            isInFeedback ? <FeedbackApp {...exports} /> :
            isInPissarra ? <PissarraApp {...exports} readonly={false} /> :
            isInNotifications ? <NotificationsDashboard {...exports} /> :
            isInGestio ? <GestioApp {...exports} /> :
            isInCalendar ? <CalendarApp {...exports} /> :
            isInForms ? <FormsApp {...exports} /> :
            isInStats ? <StatsApp {...exports} /> :
            isInEtiquetes ? <EtiquetesApp {...exports} /> :
            isInFitxar ? <FitxarApp {...exports} /> :
            isInEnllaços ? <EnllaçosApp {...exports} /> :
            isInMerxandatge ? <MerxandatgeApp {...exports} /> :
            isInQuotes ? <QuotesApp {...exports} /> :
            isInMapaAccions ? <MapaAccionsApp {...exports} /> :
            isInRols ? <RolsApp {...exports} /> :
            isInSubvencions ? <SubvencionsApp {...exports} /> :
            <ProvesApp {...exports} />
        }
    </div>)
}

export default Interface;
